import React, { useState, useRef, FormEvent, useEffect, useCallback } from "react";

import { IHandleKeyboardNavigation } from "../../types/keyboard-navigation";
import { IPersonalizedSuggestions, ISuggestionResponse } from "../../types/suggestion-response";
import { SearchBoxProps } from "../../types/searchbox";

import debounce from "../../util/debounce";
import { defaultSuggestionResponse } from "../../util/suggestions/defaultSuggestionResponse";
import { L10nContext, NavigationContext } from "../../util/contexts";
import { PageViewLogDataId } from "../../util/pageViewLogDataId";
import { sendSuggestionsEventToKraken } from "../../util/events/kraken";
import { subscribeToPriceMode, getCurrentPriceMode, PriceMode } from "../../util/priceModeObserver";
import { useKeyboardNavigation } from "../../util/suggestions/keyboard-navigation";
import { useL10n } from "../../util/l10n";
import { usePersonalizedSuggestions } from "../../util/personalization/usePersonalizedSuggestions";

import PersonalizedSuggestionList from "../suggestion-list/personalized-suggestion-list";
import SearchIcon from "../../Assets/svg/Search_2021_icon";
import SuggestionList from "../suggestion-list/suggestion-list";

import "./searchbox-megamenu.scss";
import "../suggestion-list/suggestions-megamenu.scss";
import { setStoredSearchQuery } from "../../util/queryStorage";
import { DeviceScrollEnum } from "../../types/suggestions";

const SearchBoxMegaMenu = ({ detectedContext, ...props }: SearchBoxProps) => {
    const { isWebShop, isMobileShop, device, customerType } = detectedContext;
    const { personalizedData, isLoading, isEnabled } = usePersonalizedSuggestions();

    const SEARCH_TERM_LENGTH_LIMIT = 50;
    const TOP_SEARCH_QUERIES_LIMIT = isWebShop ? 10 : 5;
    const inputTabNavAttribute = { focusable: true };

    const [focus, setFocus] = useState(false);
    const [input, setInput] = useState("");
    const [priceMode, setPriceMode] = useState<PriceMode>("gross");
    const [validSearchTerm, setValidSearchTerm] = useState("");
    const [hasValidSearchTerm, setHasValidSearchTerm] = useState(false);

    const [suggestionsVisible, setSuggestionsVisible] = useState(false);
    const [isPersonalizationAvailable, setIsPersonalizationAvailable] = useState(false);

    // for ShopSearchEnableZeroQuerySuggestions off:
    const [hasSuggestions, setHasSuggestions] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState<ISuggestionResponse>(
        defaultSuggestionResponse()
    );
    const keyboardNavListElementRef = useRef<IHandleKeyboardNavigation | null>(null);

    // for ShopSearchEnableZeroQuerySuggestions on:
    const [recoViewedEventSent, setRecoViewedEventSent] = useState(false);
    const [hasPersonalizedSuggestions, setHasPersonalizedSuggestions] = useState(false);
    const [showPersonalizedSuggestions, setShowPersonalizedSuggestions] = useState(false);
    const [personalizedSuggestions, setPersonalizedSuggestions] =
        useState<IPersonalizedSuggestions>(personalizedData);
    const keyboardNavPersonalizedListElementRef = useRef<IHandleKeyboardNavigation | null>(null);

    const scopeSswRef = useRef<HTMLDivElement | null>(null);
    const searchBoxRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const focusRef = useRef(focus);
    const suggestionsRef = useRef(suggestions);
    const hasValidSearchTermRef = useRef(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const pageViewLogDataId = PageViewLogDataId.get();

    const l10n = useL10n(props.l10n);

    useEffect(() => {
        if (searchBoxRef.current) {
            scopeSswRef.current = searchBoxRef.current.closest(
                ".scope-ssw"
            ) as HTMLDivElement | null;
        }
    }, []);

    useEffect(() => {
        focusRef.current = focus;
    }, [focus]);

    useEffect(() => {
        const isValidSearchTerm = validSearchTerm !== "";
        hasValidSearchTermRef.current = isValidSearchTerm;
        setHasValidSearchTerm(isValidSearchTerm);
    }, [validSearchTerm]);

    useEffect(() => {
        const isAvailable = !isLoading && isEnabled;
        setPersonalizedSuggestions({ ...personalizedData });
        setIsPersonalizationAvailable(isAvailable);
    }, [isLoading, isEnabled, personalizedData]);

    useEffect(() => {
        const isValidSearchWithoutSuggestions = !hasPersonalizedSuggestions && hasValidSearchTerm;
        const shouldBeShown =
            isPersonalizationAvailable && focus && !isValidSearchWithoutSuggestions;
        setShowPersonalizedSuggestions(shouldBeShown);
    }, [isPersonalizationAvailable, hasPersonalizedSuggestions, hasValidSearchTerm, focus]);

    useEffect(() => {
        const shouldBeShown = hasSuggestions && focus && hasValidSearchTerm;
        setShowSuggestions(shouldBeShown);
    }, [hasSuggestions, focus, hasValidSearchTerm]);

    useEffect(() => {
        setSuggestionsVisible(showSuggestions || showPersonalizedSuggestions);
        if (showPersonalizedSuggestions) {
            setRecoViewedEventSent(true);
        }
    }, [showSuggestions, showPersonalizedSuggestions]);

    useEffect(() => {
        suggestionsRef.current = suggestions;
    }, [suggestions]);

    useEffect(() => {
        const hasSuggestions =
            suggestions.articles.length > 0 ||
            suggestions.flexibleSets.length > 0 ||
            suggestions.contents.length > 0 ||
            suggestions.topSearchQueries.length > 0;
        setHasSuggestions(hasSuggestions);
    }, [suggestions]);

    useEffect(() => {
        const hasPersonalizedData =
            personalizedSuggestions.articles.length > 0 ||
            personalizedSuggestions.flexibleSets.length > 0 ||
            personalizedSuggestions.contents.length > 0 ||
            personalizedSuggestions.topSearchQueries.length > 0 ||
            personalizedSuggestions.storedQueries.length > 0;
        setHasPersonalizedSuggestions(hasPersonalizedData);
    }, [personalizedSuggestions]);

    useEffect(() => {
        setPriceMode(getCurrentPriceMode());
        const unsubscribe = subscribeToPriceMode((priceMode) => setPriceMode(priceMode));
        return unsubscribe;
    }, []);

    const handleClick = useCallback((event: Event) => {
        const target = event.target as HTMLElement;

        if (!searchBoxRef.current?.contains(target)) handleBlur();
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleClick);

        return () => {
            document.removeEventListener("mousedown", handleClick);
        };
    }, [handleClick]);

    const personalizedNavApi = useKeyboardNavigation(
        () => keyboardNavPersonalizedListElementRef.current?.getNavigationElements() ?? []
    );

    const navigationApi = useKeyboardNavigation(
        () => keyboardNavListElementRef.current?.getNavigationElements() ?? []
    );

    useEffect(() => {
        const suggestionsVisible = hasSuggestions && showSuggestions;
        const hasElements = keyboardNavListElementRef.current?.hasNavigationElements();
        if (!suggestionsVisible && hasElements) {
            navigationApi.resetNavigation();
        }
    }, [hasSuggestions, showSuggestions, navigationApi]);

    useEffect(() => {
        const suggestionsVisible = hasPersonalizedSuggestions && showPersonalizedSuggestions;
        const hasElements = keyboardNavPersonalizedListElementRef.current?.hasNavigationElements();
        if (!suggestionsVisible && hasElements) {
            personalizedNavApi.resetNavigation();
        }
    }, [hasPersonalizedSuggestions, showPersonalizedSuggestions, personalizedNavApi]);

    const handleBlur = () => {
        if (!focusRef.current) return;

        setFocus(false);
        inputRef.current?.blur();
    };

    const handleFocus = () => {
        if (focusRef.current) return;
        setFocus(true);
    };

    const activateFocusHelper = () => {
        handleFocus();
        inputRef.current?.focus();
    };

    const resetSuggestionResponse = () => {
        setSuggestions(defaultSuggestionResponse());
    };

    const resetPersonalizedSuggestionResponse = () => {
        setPersonalizedSuggestions({ ...personalizedData });
    };

    const fetchSuggestions = async (input: string) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const headers: Record<string, string> = pageViewLogDataId
            ? { "X-PageView-ID": pageViewLogDataId }
            : {};

        try {
            const res = await fetch(
                `${props.apiUrl}suggestion/?searchTerm=${encodeURIComponent(
                    input
                )}&topSearchQueriesLimit=${TOP_SEARCH_QUERIES_LIMIT}`,
                { headers, signal: controller.signal }
            );

            if (!res.ok) throw new Error("Network response was not ok");

            const response = await res.json();

            if (
                response.articles.length ||
                response.flexibleSets.length ||
                response.contents.length ||
                response.topSearchQueries.length
            ) {
                const newSuggestions = response as ISuggestionResponse;

                const handleEmptySearchTermEdgeCase = !hasValidSearchTermRef.current;
                if (handleEmptySearchTermEdgeCase) return;

                if (JSON.stringify(newSuggestions) !== JSON.stringify(suggestionsRef.current)) {
                    sendSuggestionsEventToKraken(input);
                }

                setSuggestions(newSuggestions);
                setPersonalizedSuggestions({
                    ...newSuggestions,
                    isSuggestionResponse: true,
                    storedQueries: []
                });
            } else {
                resetSuggestionResponse();
                resetPersonalizedSuggestionResponse();
            }
        } catch (error) {
            if (error instanceof Error && error.name !== "AbortError") {
                resetSuggestionResponse();
                resetPersonalizedSuggestionResponse();
            }
        } finally {
            abortControllerRef.current = null;
        }
    };

    const debouncedFetch = useRef<((input: string) => void) & { cancel: () => void }>(
        debounce((input: string) => {
            fetchSuggestions(input);
        }, 250)
    );

    const sanatizeInput = (input: string) => {
        const validInput = input.length > SEARCH_TERM_LENGTH_LIMIT ? input.slice(0, 50) : input;
        return validInput;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value ?? "";
        const sanatizedInput = sanatizeInput(value);
        setInput(sanatizedInput);

        const trimmedInput = sanatizedInput.trim();
        if (trimmedInput === "") {
            setValidSearchTerm("");
            debouncedFetch.current.cancel();
            resetSuggestionResponse();
            resetPersonalizedSuggestionResponse();
        } else {
            setValidSearchTerm(trimmedInput);
            debouncedFetch.current(trimmedInput);
        }
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const trimmedInput = input.trim();
        if (!trimmedInput) {
            handleBlur();
            return;
        }

        const target = `${props.searchTarget}?query=${encodeURIComponent(trimmedInput)}`;
        if (isPersonalizationAvailable) setStoredSearchQuery({ name: trimmedInput, target });
        window.location.href = target;
    };

    return (
        <L10nContext.Provider value={l10n}>
            <div
                className={
                    "searchbox-megamenu" +
                    ` ssw-current-pricemode-${priceMode}` +
                    ` ${focus ? "focus" : ""}` +
                    ` ${suggestionsVisible ? "show-suggestions" : ""}` +
                    ` ${props.tenant}` +
                    " esApp"
                }
                ref={searchBoxRef}
            >
                <form
                    className={"search-megamenu"}
                    onSubmit={handleSubmit}
                    onClick={activateFocusHelper}
                >
                    <button
                        type="submit"
                        className="search-megamenu-button"
                        aria-label={l10n.defaultTerm}
                    >
                        <SearchIcon />
                    </button>

                    <span className="transition-wrapper">
                        <span
                            className="input-sizer"
                            data-value={validSearchTerm || (focus ? "" : l10n.defaultTerm)}
                        >
                            <input
                                ref={inputRef}
                                value={input}
                                maxLength={50}
                                onChange={handleChange}
                                onFocus={handleFocus}
                                placeholder={focus ? "" : l10n.defaultTerm}
                                className="search-megamenu-input"
                                type="text"
                                name="query"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck="false"
                                aria-label={l10n.defaultTerm}
                                size={1}
                                {...inputTabNavAttribute}
                            />
                        </span>
                    </span>
                </form>

                <hr className={"grid-line"} />

                <div id={`new-suggestions-${props.view}`} className={"new-suggestions"}>
                    {isPersonalizationAvailable
                        ? showPersonalizedSuggestions && (
                              <NavigationContext.Provider value={personalizedNavApi}>
                                  <PersonalizedSuggestionList
                                      navigationRef={keyboardNavPersonalizedListElementRef}
                                      isPersonalizedData={
                                          !personalizedSuggestions.isSuggestionResponse
                                      }
                                      portal={props.portal}
                                      culture={props.culture}
                                      searchTarget={props.searchTarget}
                                      trackingEndpoint={props.trackingEndpoint}
                                      articles={personalizedSuggestions.articles}
                                      flexibleSets={personalizedSuggestions.flexibleSets}
                                      storedQueries={personalizedSuggestions.storedQueries}
                                      contents={personalizedSuggestions.contents}
                                      featurePromotion={personalizedSuggestions.featurePromotion}
                                      topSearchQueries={personalizedSuggestions.topSearchQueries}
                                      priceMandantSettings={
                                          personalizedSuggestions.priceMandantSettingsArticles
                                      }
                                      priceMandantSettingsV2={
                                          personalizedSuggestions.priceMandantSettingsFlexibleSet
                                      }
                                      customerType={customerType}
                                      searchTerm={validSearchTerm}
                                      isMobileShop={isMobileShop}
                                      view={props.view}
                                      pageViewLogDataId={pageViewLogDataId}
                                      deviceScrollClass={DeviceScrollEnum.None}
                                      deviceType={device}
                                      recoViewedEventSent={recoViewedEventSent}
                                  />
                              </NavigationContext.Provider>
                          )
                        : showSuggestions && (
                              <NavigationContext.Provider value={navigationApi}>
                                  <SuggestionList
                                      navigationRef={keyboardNavListElementRef}
                                      portal={props.portal}
                                      culture={props.culture}
                                      searchTarget={props.searchTarget}
                                      trackingEndpoint={props.trackingEndpoint}
                                      articles={suggestions.articles}
                                      flexibleSets={suggestions.flexibleSets}
                                      contents={suggestions.contents}
                                      featurePromotion={suggestions.featurePromotion}
                                      topSearchQueries={suggestions.topSearchQueries}
                                      priceMandantSettings={
                                          suggestions.priceMandantSettingsArticles
                                      }
                                      priceMandantSettingsV2={
                                          suggestions.priceMandantSettingsFlexibleSet
                                      }
                                      customerType={customerType}
                                      searchTerm={validSearchTerm}
                                      isMobileShop={isMobileShop}
                                      view={props.view}
                                      pageViewLogDataId={pageViewLogDataId}
                                      deviceType={device}
                                  />
                              </NavigationContext.Provider>
                          )}
                </div>
            </div>
        </L10nContext.Provider>
    );
};

export default SearchBoxMegaMenu;
